// src/lib/baseApi.ts

import axios from "axios";
import { getDeviceIdAsync } from "../utils/keyManager";
import { attachHttpLogging } from "../utils/logging/attachHttpLogging";
import { authReady } from "../lib/authBoot";

const BASE_URL = (process.env.NEXT_PUBLIC_BASE_URL as string | undefined) || "/api";
const isBrowser = typeof window !== "undefined";

const baseApi = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

// REQUEST: add tokens + device id
baseApi.interceptors.request.use(
  async (config) => {
    await authReady;
    if (isBrowser) {
      const accessToken = window.localStorage.getItem("access_token");
      if (accessToken) {
        config.headers = config.headers ?? {};
        config.headers["Authorization"] = `Bearer ${accessToken}`;
      }
    }
    if (process.env.NODE_ENV === "production") {
      config.headers = config.headers ?? {};
      config.headers["X-Internal-Request"] = "yes";
    }
    try {
      if (isBrowser) {
        const devId = await getDeviceIdAsync().catch(() => "");
        if (devId && devId !== "server" && devId !== "unknown") {
          config.headers = config.headers ?? {};
          config.headers["X-Device-ID"] = devId.trim().toLowerCase();
        }
      }
    } catch {
      // ignore
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// RESPONSE: refresh flow (keep as-is)
baseApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest: any = error.config;
    if (!error.response) return Promise.reject(error);

    if (error.response.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      if (isBrowser) {
        const refreshToken = window.localStorage.getItem("refresh_token");
        if (refreshToken) {
          try {
            const devId = await getDeviceIdAsync().catch(() => "");
            const resp = await axios.post(
              `${BASE_URL}/auth/refresh/`,
              { refresh: refreshToken },
              { headers: devId ? { "X-Device-ID": devId } : undefined }
            );
            const newAccessToken = resp.data.access;
            window.localStorage.setItem("access_token", newAccessToken);

            originalRequest.headers = originalRequest.headers ?? {};
            originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
            return baseApi(originalRequest);
          } catch (refreshError) {
            window.localStorage.removeItem("access_token");
            window.localStorage.removeItem("refresh_token");
            if (typeof window !== "undefined") window.location.href = "/";
            return Promise.reject(refreshError);
          }
        }
      }
    }
    return Promise.reject(error);
  }
);

// 🚧 IMPORTANT: attach logging AFTER refresh interceptor
attachHttpLogging(baseApi, "baseApi");

export default baseApi;
